import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { EventTypeEnum, LoginErrorPageParams } from '../../segment/types';
import { useAppDependenciesContext } from '../../state';
import { requireKeys } from '../../utils';
import {
  CUSTOM_REDIRECT_URL_QUERY_PARAM_KEYS,
  CustomRedirectUrlQueryParams,
  getCustomRedirectUrlQueryParams,
} from '../redirect/redirectUrl';
import { useLoginWithRedirectCallback } from '../redirect/useLoginWithRedirectCallback';
import { loginExperienceToUserType } from '../types';
import { CustomRedirectUrlParamsNotFoundError } from './CustomRedirectUrlParamsNotFoundError';
import { LoginErrorPageViewModel } from './viewModel';

function validateCustomRedirectUrlQueryParams(queryString: string): CustomRedirectUrlQueryParams {
  const redirectUrlQueryParams = getCustomRedirectUrlQueryParams(queryString);
  const [requiredRedirectUrlQueryParams, missingKeys] = requireKeys(
    redirectUrlQueryParams,
    CUSTOM_REDIRECT_URL_QUERY_PARAM_KEYS,
  );

  if (!requiredRedirectUrlQueryParams) {
    throw new CustomRedirectUrlParamsNotFoundError({
      missingKeys,
    });
  }

  return requiredRedirectUrlQueryParams;
}

function useSegmentTracking({ returnTo, loginExperience }: CustomRedirectUrlQueryParams) {
  const authError: Error | undefined = useLocation().state?.authError;
  const authErrorCode = authError && `${authError}`;
  const { useTrackEvent } = useAppDependenciesContext();
  const { userViewed, userClicked } = useTrackEvent();

  const options: LoginErrorPageParams = useMemo(
    () => ({
      entityType: loginExperienceToUserType(loginExperience),
      errorCode: authErrorCode || 'unknown_error',
      source: returnTo,
    }),
    [authErrorCode, loginExperience, returnTo],
  );

  const trackUserRetriedLogin = useCallback(() => {
    userClicked(EventTypeEnum.RETRY_LOGIN_CLICKED, options);
  }, [options, userClicked]);

  useEffect(
    function trackUserViewedOnMount() {
      userViewed(EventTypeEnum.LOGIN_ERROR_PAGE_VIEWED, options);
    },
    [options, userViewed],
  );

  return {
    trackUserRetriedLogin,
  };
}

export function useLoginErrorPageViewModel(): LoginErrorPageViewModel {
  const [queryString] = useState(useLocation().search);
  const customRedirectUrlQueryParams = validateCustomRedirectUrlQueryParams(queryString);
  const handleLogin = useLoginWithRedirectCallback();
  const { trackUserRetriedLogin } = useSegmentTracking(customRedirectUrlQueryParams);

  const onLogin = useCallback(() => {
    trackUserRetriedLogin();
    handleLogin({
      ...customRedirectUrlQueryParams,
      openUrl(url) {
        // replacing to mitigate navigating back to error page
        window.location.replace(url);
      },
    });
  }, [customRedirectUrlQueryParams, handleLogin, trackUserRetriedLogin]);

  return {
    onLogin,
  };
}
